import { crearTabla, mostrarToast } from '@plataforma/ui';

import {
  crearPromocion,
  listarPromocionesBackoffice,
  listarPlanesBackoffice,
} from '../api/backoffice.js';
import { exigirSuperAdmin } from '../guardas.js';

import {
  crearBadge,
  crearPaginacion,
  estadoCarga,
  estadoError,
  fecha,
  moneda,
} from './backoffice-ui.js';
import { montarShell } from './shell.js';

const ESTADOS_PROMOCION = {
  activa: { etiqueta: 'Activa', tono: 'exito' },
  inactiva: { etiqueta: 'Inactiva', tono: 'neutral' },
  expirada: { etiqueta: 'Expirada', tono: 'alerta' },
};

export async function vistaPromociones(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/promociones');
  const estadoVista = { soloActivas: false, pagina: 1 };

  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Promociones</h1>
        <p class="pf-texto-suave">Descuentos por tiempo limitado aplicables a planes específicos.</p>
      </div>
      <div class="pf-page-header__acciones">
        <button type="button" class="pf-boton pf-boton--primario" id="pf-nueva-promocion">
          Nueva promoción
        </button>
      </div>
    </div>
    <form class="pf-tarjeta pf-backoffice-toolbar" id="pf-filtros-promociones">
      <div class="pf-campo">
        <label for="pf-solo-activas">
          <input type="checkbox" id="pf-solo-activas" /> Solo activas
        </label>
      </div>
      <button type="submit" class="pf-boton pf-boton--secundario">Filtrar</button>
    </form>
    <div id="pf-promociones-tabla">${estadoCarga()}</div>
  `;

  contenido.querySelector('#pf-filtros-promociones').addEventListener('submit', async (e) => {
    e.preventDefault();
    estadoVista.soloActivas = contenido.querySelector('#pf-solo-activas').checked;
    estadoVista.pagina = 1;
    await cargar(contenido, estadoVista);
  });

  contenido.querySelector('#pf-nueva-promocion').addEventListener('click', () => {
    abrirModalCrear(contenido);
  });

  await cargar(contenido, estadoVista);
}

async function cargar(contenido, estadoVista) {
  const destino = contenido.querySelector('#pf-promociones-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error, count } = await listarPromocionesBackoffice(estadoVista);
  if (error) {
    destino.innerHTML = estadoError('No se pudieron cargar las promociones.');
    return;
  }

  destino.innerHTML = '';
  destino.appendChild(
    crearTabla({
      filaVacia: 'No hay promociones registradas.',
      columnas: [
        { titulo: 'Nombre', render: (f) => f.nombre },
        {
          titulo: 'Tipo',
          render: (f) =>
            f.tipo_descuento === 'porcentual' ? `${f.valor_descuento}%` : moneda(f.valor_descuento),
        },
        { titulo: 'Inicio', render: (f) => fecha(f.fecha_inicio) },
        { titulo: 'Fin', render: (f) => fecha(f.fecha_fin) },
        { titulo: 'Estado', render: (f) => crearBadge(ESTADOS_PROMOCION, f.estado) },
        { titulo: 'Creada', render: (f) => fecha(f.created_at) },
      ],
      filas: data ?? [],
    }),
  );
  destino.appendChild(
    crearPaginacion({
      total: count ?? 0,
      pagina: estadoVista.pagina,
      alCambiar: async (pagina) => {
        estadoVista.pagina = pagina;
        await cargar(contenido, estadoVista);
      },
    }),
  );
}

async function abrirModalCrear(contenedor) {
  let planes = [];
  try {
    const result = await listarPlanesBackoffice();
    planes = result.data ?? [];
  } catch {
    /* silently proceed without plan association */
  }

  const modal = document.createElement('div');
  modal.className = 'pf-modal-overlay';
  modal.innerHTML = `
    <div class="pf-tarjeta pf-modal" style="max-width:560px;">
      <h2 style="margin:0 0 16px;">Nueva promoción</h2>
      <form id="pf-form-promocion" style="display:flex;flex-direction:column;gap:12px;">
        <div class="pf-campo">
          <label for="pf-prom-nombre">Nombre</label>
          <input id="pf-prom-nombre" type="text" required placeholder="Ej: Black Friday 2026" />
        </div>
        <div class="pf-campo">
          <label for="pf-prom-descripcion">Descripción</label>
          <textarea id="pf-prom-descripcion" rows="2"></textarea>
        </div>
        <div class="pf-fila" style="gap:12px;">
          <div class="pf-campo" style="flex:1;">
            <label for="pf-prom-tipo">Tipo descuento</label>
            <select id="pf-prom-tipo" required>
              <option value="porcentual">Porcentual (%)</option>
              <option value="fijo">Fijo (S/)</option>
            </select>
          </div>
          <div class="pf-campo" style="flex:1;">
            <label for="pf-prom-valor">Valor</label>
            <input id="pf-prom-valor" type="number" min="0.01" step="0.01" required />
          </div>
        </div>
        <div class="pf-fila" style="gap:12px;">
          <div class="pf-campo" style="flex:1;">
            <label for="pf-prom-inicio">Fecha inicio</label>
            <input id="pf-prom-inicio" type="datetime-local" required />
          </div>
          <div class="pf-campo" style="flex:1;">
            <label for="pf-prom-fin">Fecha fin</label>
            <input id="pf-prom-fin" type="datetime-local" required />
          </div>
        </div>
        <div class="pf-campo">
          <label>Planes (opcional)</label>
          <div style="display:flex;flex-wrap:wrap;gap:8px;">
            ${planes
              .filter((p) => p.estado === 'activo')
              .map(
                (p) => `
              <label style="display:flex;align-items:center;gap:4px;">
                <input type="checkbox" name="pf-prom-planes" value="${p.id}" />
                ${p.nombre} (S/${Number(p.precio_mensual).toFixed(2)})
              </label>`,
              )
              .join('')}
          </div>
        </div>
        <div class="pf-fila" style="justify-content:flex-end;gap:8px;">
          <button type="button" class="pf-boton pf-boton--secundario" id="pf-prom-cancelar">Cancelar</button>
          <button type="submit" class="pf-boton pf-boton--primario">Crear promoción</button>
        </div>
      </form>
    </div>
  `;

  contenedor.appendChild(modal);

  modal.querySelector('#pf-prom-cancelar').addEventListener('click', () => modal.remove());
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.remove();
  });

  modal.querySelector('#pf-form-promocion').addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('[type="submit"]');
    btn.disabled = true;

    const planIds = [...modal.querySelectorAll('input[name="pf-prom-planes"]:checked')].map(
      (cb) => cb.value,
    );

    const { error } = await crearPromocion({
      nombre: modal.querySelector('#pf-prom-nombre').value.trim(),
      descripcion: modal.querySelector('#pf-prom-descripcion').value.trim() || null,
      tipo_descuento: modal.querySelector('#pf-prom-tipo').value,
      valor_descuento: modal.querySelector('#pf-prom-valor').value,
      fecha_inicio: new Date(modal.querySelector('#pf-prom-inicio').value).toISOString(),
      fecha_fin: new Date(modal.querySelector('#pf-prom-fin').value).toISOString(),
      plan_ids: planIds.length > 0 ? planIds : null,
    });

    btn.disabled = false;
    if (error) {
      mostrarToast(`Error: ${error.message}`, { tono: 'peligro' });
      return;
    }

    mostrarToast('Promoción creada.', { tono: 'exito' });
    modal.remove();
  });
}
